import { useState } from "react";
import { Send, User, Phone, MapPin, MessageSquare, Loader2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingCTA from "../components/FloatingCTA";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", phone: "", city: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact – Serrurier Lieusaint | 06 68 03 25 61"
        description="Contactez Serrurier Lieusaint pour tous vos besoins en serrurerie. Formulaire de contact, téléphone, email. Disponible 24h/24 pour les urgences."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-zinc-950">
          <div className="container-custom">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-zinc-400 mb-8">
              <Link to="/" className="hover:text-yellow-400 transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-yellow-400">Contact</span>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-6 font-heading" data-testid="contact-page-title">
                Contactez-nous
              </h1>
              <p className="text-lg text-zinc-300">
                Besoin d'un serrurier ? Contactez-nous par téléphone pour une intervention rapide 
                ou remplissez le formulaire ci-dessous.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Left column - Info */}
              <div>
                <h2 className="text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-8 font-heading">
                  Nos coordonnées
                </h2>

                <div className="space-y-6 mb-10">
                  <div className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div className="w-12 h-12 bg-yellow-400 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-900 mb-1">Téléphone</h3>
                      <a href="tel:0668032561" className="text-xl font-bold text-yellow-500 hover:text-yellow-600">
                        06 68 03 25 61
                      </a>
                      <p className="text-sm text-zinc-500 mt-1">Disponible 24h/24 – 7j/7</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div className="w-12 h-12 bg-yellow-400 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-900 mb-1">Email</h3>
                      <a href="mailto:serrurierlieusaint@gmail.com" className="text-yellow-500 hover:text-yellow-600">
                        serrurierlieusaint@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div className="w-12 h-12 bg-yellow-400 rounded-sm flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zinc-900 mb-1">Adresse</h3>
                      <p className="text-zinc-600">
                        23 Avenue René Cassin<br />
                        77127 Lieusaint
                      </p>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="rounded-sm overflow-hidden border border-zinc-200 shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2638.4!2d2.5494!3d48.6279!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e5e3e9c9c9c9c9%3A0x0!2s23%20Avenue%20Ren%C3%A9%20Cassin%2C%2077127%20Lieusaint!5e0!3m2!1sfr!2sfr!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Serrurier Lieusaint - Localisation"
                  />
                </div>
              </div>

              {/* Right column - Form */}
              <div>
                <h2 className="text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-8 font-heading">
                  Envoyez-nous un message
                </h2>
                
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2 text-zinc-700">
                        <User className="w-4 h-4 text-yellow-500" />
                        Nom
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Votre nom"
                        className="h-12 bg-white border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm"
                        data-testid="contact-page-name-input"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2 text-zinc-700">
                        <Phone className="w-4 h-4 text-yellow-500" />
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Votre numéro de téléphone"
                        className="h-12 bg-white border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm"
                        data-testid="contact-page-phone-input"
                      />
                    </div>

                    {/* City */}
                    <div className="space-y-2">
                      <Label htmlFor="city" className="flex items-center gap-2 text-zinc-700">
                        <MapPin className="w-4 h-4 text-yellow-500" />
                        Ville
                      </Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Votre ville"
                        className="h-12 bg-white border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm"
                        data-testid="contact-page-city-input"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="flex items-center gap-2 text-zinc-700">
                        <MessageSquare className="w-4 h-4 text-yellow-500" />
                        Message
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre besoin..."
                        rows={5}
                        className="bg-white border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm resize-none"
                        data-testid="contact-page-message-input"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide py-6 rounded-sm"
                      data-testid="contact-page-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </Button>

                    {/* Status messages */}
                    {submitStatus === "success" && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-sm text-green-700 text-center">
                        Message envoyé avec succès ! Nous vous recontactons rapidement.
                      </div>
                    )}
                    {submitStatus === "error" && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-center">
                        Une erreur est survenue. Veuillez appeler le 06 68 03 25 61.
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </>
  );
};

export default Contact;
